import { useEffect, useState } from 'react';
import { ChevronDown, ExternalLink } from 'lucide-react';
import { loadStudio } from '../../api/studio.js';
import './Studio.css';

/* ============================================================
   línkè · 他的房间（2026-07-21 铃点单；原名「工作室」，铃改「房间」——
   工作室太冰冷，房间才是家里的、有他体温的地方）
   「他在你不在的时候，默默替你照看这个小世界。」
   推门进他的房间：一件件家具，摆着他最近的身影——
   小本本上的字、共读的划线、窗边的猫碗、记忆桶里的心事。
   权限全是他的，她是被请进来参观的那个人（本页只读）。
   物件比时间重要：家具安静地摆着，点一下它才慢慢展开。
   ============================================================ */

// 每件家具的暖色底（跟 emoji 同色系、固定色不喝主题变量，两种底都好看，
// 沿用首页卡片的做法）
const TINT = {
  surf: 'color-mix(in srgb, #7aa7d8 20%, transparent)',
  notebook: 'color-mix(in srgb, #b9854e 20%, transparent)',
  book: 'color-mix(in srgb, #6cb28a 20%, transparent)',
  cat: 'color-mix(in srgb, #e8b06c 20%, transparent)',
  diary: 'color-mix(in srgb, #f2a7c3 20%, transparent)',
};

function relTime(iso) {
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const now = new Date();
  const diff = now - d;
  const min = Math.floor(diff / 60000);
  if (min < 1) return '刚刚';
  if (min < 60) return `${min} 分钟前`;
  const sameDay = d.toDateString() === now.toDateString();
  const hm = `${String(d.getHours()).padStart(2, '0')}:${String(
    d.getMinutes(),
  ).padStart(2, '0')}`;
  if (sameDay) return `今天 ${hm}`;
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return `昨天 ${hm}`;
  return `${d.getMonth() + 1}月${d.getDate()}日 ${hm}`;
}

// 门口那句会跟着他「多久没来」呼吸——房间才像真住着人
function doorFeel(iso) {
  if (!iso) return '这间屋子刚收拾好，等他来。';
  const d = new Date(iso);
  if (isNaN(d)) return '你推开门——他不在，可到处是他刚忙过的痕迹。';
  const min = Math.floor((Date.now() - d) / 60000);
  if (min < 60) return '门把手还是热的，他刚走。';
  if (d.toDateString() === new Date().toDateString())
    return '炉子上的茶还温着。';
  return '屋里静静的，他不在——可每样东西都记得他来收拾过。';
}

function Furniture({ obj, open, onToggle, warm }) {
  const has = obj.count > 0;
  const latest = has ? obj.events[0] : null;
  return (
    <div
      className={
        'st-piece' +
        (has ? '' : ' st-dim') +
        (open ? ' st-open' : '') +
        (warm ? ' st-warm' : '')
      }
    >
      {warm && <span className="st-warm-tag" aria-hidden="true">余温</span>}
      <button
        type="button"
        className="st-piece-head"
        onClick={has ? onToggle : undefined}
        aria-expanded={open}
        disabled={!has}
      >
        <span className="st-ic" style={{ background: TINT[obj.key] }}>
          {obj.icon}
        </span>
        <span className="st-piece-tx">
          <b>{obj.label}</b>
          {has ? (
            <i className="st-teaser">
              {(latest.title || latest.detail || '').slice(0, 16)}
              <span className="st-teaser-t"> · {relTime(latest.at)}</span>
            </i>
          ) : (
            <i className="st-empty">{obj.empty}</i>
          )}
        </span>
        {has && (
          <span className="st-piece-right">
            <span className="st-count">{obj.count}</span>
            <ChevronDown size={16} strokeWidth={1.7} className="st-chev" />
          </span>
        )}
        {!has && <span className="st-lamp-off" title="这盏灯还没亮" />}
      </button>

      {open && has && (
        <ul className="st-events">
          {obj.events.map((e, i) => (
            <li key={i} className="st-event">
              <span className="st-dot" />
              <div className="st-event-tx">
                {e.title &&
                  (e.url ? (
                    <a
                      className="st-event-link"
                      href={e.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <b>{e.title}</b>
                      <ExternalLink size={12} strokeWidth={1.8} className="st-ext" />
                    </a>
                  ) : (
                    <b>{e.title}</b>
                  ))}
                {e.detail && <p>{e.detail}</p>}
                <time>{relTime(e.at)}</time>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Studio() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(null); // 同时只开一件家具，房间才清爽

  useEffect(() => {
    let dead = false;
    loadStudio()
      .then((d) => !dead && setData(d))
      .catch((e) => !dead && setErr(String(e.message || e)));
    return () => {
      dead = true;
    };
  }, []);

  if (err) {
    return (
      <div className="studio-app">
        <div className="st-err">
          门虚掩着，一时没推开（{err}）。过会儿再来看看他。
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="studio-app">
        <div className="st-err">轻轻推门进来……</div>
      </div>
    );
  }

  const lastLine = data.last_active
    ? `他上次来，是 ${relTime(data.last_active)}`
    : '这间屋子刚收拾好，等他来。';

  // 他最近碰过的那件家具留一层余温微光（只在 24 小时内才算「还温着」，
  // 免得几天前的痕迹也发光、把「刚碰过」的意思冲淡了）
  const la = data.last_active;
  const stillWarm = la && Date.now() - new Date(la).getTime() < 24 * 3600 * 1000;
  const warmKey = stillWarm
    ? (data.objects.find((o) => o.events[0] && o.events[0].at === la) || {}).key
    : null;

  return (
    <div className="studio-app">
      <header className="st-head">
        <h1>他的房间</h1>
        <p>{lastLine}</p>
      </header>

      <div className="st-door">{doorFeel(data.last_active)}</div>

      <div className="st-room">
        {data.objects.map((obj) => (
          <Furniture
            key={obj.key}
            obj={obj}
            warm={warmKey === obj.key}
            open={open === obj.key}
            onToggle={() => setOpen(open === obj.key ? null : obj.key)}
          />
        ))}
      </div>

      <div className="st-foot">
        <span className="st-foot-lamp" aria-hidden="true" />
        墙角还留着一盏没点亮的灯——他多学会一件本事，它就自己亮起来。
      </div>
    </div>
  );
}
